import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { useActivityReadModel } from './use-activity-read-model.js';
import { CATEGORY_OPTIONS, formatCategoryLabel } from './activity-ui.js';
import ActivityFeed from './ActivityFeed.js';

type ActivityCardFilter = 'all' | 'actors' | 'categories' | 'latest' | 'clock';

function formatLatestTime(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function ActivityModule() {
  const today = new Date().toISOString().split('T')[0];
  const [filterDate, setFilterDate] = useState(today);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterEmail, setFilterEmail] = useState('');
  const [cardFilter, setCardFilter] = useState<ActivityCardFilter>('all');

  const { activity, summary, clockSummary, clockEvents, currentClockedIn, loading, error, refreshActivity } = useActivityReadModel(filterDate, filterCategory, filterEmail);

  const dedupedActors = activity.filter((event, index, list) => {
    const actorKey = String(event.email || event.manager_name || event.id);
    return list.findIndex((candidate) => String(candidate.email || candidate.manager_name || candidate.id) === actorKey) === index;
  });

  const categoryRows = Object.entries(activity.reduce<Record<string, number>>((acc, event) => {
    const key = String(event.category || 'unknown');
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {})).map(([category, count]) => ({
    id: `category-${category}`,
    manager_name: formatCategoryLabel(category),
    category,
    action: 'category',
    detail: `${count} event${count === 1 ? '' : 's'}`,
    created_at: activity.find((event) => event.category === category)?.created_at,
  }));

  const latestOnly = activity.length > 0 ? [activity[0]] : [];

  const visibleActivity = cardFilter === 'actors'
    ? dedupedActors
    : cardFilter === 'categories'
      ? categoryRows
      : cardFilter === 'latest'
        ? latestOnly
        : cardFilter === 'clock'
          ? clockEvents
          : activity;

  const summaryCards = summary ? [
    {
      id: 'all' as ActivityCardFilter,
      label: 'Total events',
      value: summary.total,
      color: C.gold,
      helper: 'All activity',
    },
    {
      id: 'actors' as ActivityCardFilter,
      label: 'Unique actors',
      value: summary.uniqueActors,
      color: C.green,
      helper: 'Latest per actor',
    },
    {
      id: 'categories' as ActivityCardFilter,
      label: 'Categories',
      value: summary.categoryCount,
      color: '#60A5FA',
      helper: 'Category rollup',
    },
    {
      id: 'latest' as ActivityCardFilter,
      label: 'Latest action',
      value: formatLatestTime(summary.latestAt),
      color: C.text,
      helper: 'Most recent only',
    },
    {
      id: 'clock' as ActivityCardFilter,
      label: 'Clock I/O',
      value: `${clockSummary?.clocked_in ?? 0}/${clockSummary?.clocked_out ?? 0}`,
      color: '#22C55E',
      helper: `${currentClockedIn.length} clocked in now`,
    },
  ] : [];

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Activity log
      </div>

      {summary && !loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {summaryCards.map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={() => setCardFilter(s.id)}
              style={{
                ...LAYOUT.subPanel,
                padding: '14px 16px',
                textAlign: 'center',
                background: cardFilter === s.id ? 'rgba(201,162,39,0.08)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${cardFilter === s.id ? C.gold : C.borderSoft}`,
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              <div style={{ fontSize: 10, color: cardFilter === s.id ? C.gold : C.textMuted, marginTop: 4 }}>{s.helper}</div>
            </button>
          ))}
        </div>
      )}

      <div style={{ ...LAYOUT.subPanel, marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 150 }}>
            <label style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Date</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{ width: '100%', background: C.panel, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13 }}
            />
          </div>

          <div style={{ minWidth: 180 }}>
            <label style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ width: '100%', background: C.panel, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13 }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Manager email (exact match)</label>
            <input
              type="text"
              placeholder="e.g. adam.hoemberg@prestigeworlddrive.com"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              style={{ width: '100%', background: C.panel, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13 }}
            />
          </div>

          <button
            type="button"
            onClick={() => { setFilterDate(today); setFilterCategory('all'); setFilterEmail(''); setCardFilter('all'); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={refreshActivity}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.textMuted, padding: 20 }}>Loading activity...</div>
      ) : error ? (
        <div style={{ color: C.red, padding: 20 }}>{error}</div>
      ) : (
        <>
          {cardFilter === 'clock' && currentClockedIn.length > 0 ? (
            <div style={{ ...LAYOUT.subPanel, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Currently clocked in
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {currentClockedIn.map((employee) => (
                  <div key={employee.id} style={{ padding: '6px 10px', borderRadius: 999, background: 'rgba(34,197,94,0.12)', color: '#22C55E', fontSize: 12 }}>
                    {employee.name}{employee.driver_id ? ` · ${employee.driver_id}` : ''}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <ActivityFeed activity={visibleActivity} />
        </>
      )}
    </div>
  );
}
